/**
 * PropertyGrid - Core Reusable Property Grid Component
 * 
 * A modern, Airbnb-style property grid that displays properties in a clean layout.
 * This is a SCORABLE component - theme-agnostic and reusable across all themes.
 * 
 * Features:
 * - Responsive grid layout (1, 2, or 3 columns)
 * - Configurable rows
 * - Clean, minimal design
 * - Customizable via className and renderCard props
 */

'use client';

import type { Property } from '@/lib/api/clients/properties';
import { motion } from 'framer-motion';
import React from 'react';

export interface PropertyGridProps {
  /**
   * Array of properties to display
   */
  properties: Property[];
  
  /**
   * Number of columns in the grid
   * @default 3
   */
  columns?: 1 | 2 | 3 | 4;
  
  /**
   * Maximum number of rows to display
   * @default undefined (show all)
   */
  maxRows?: number;
  
  /**
   * Custom className for the grid container
   */
  className?: string;
  
  /**
   * Custom render function for each property card
   * If not provided, uses the default card renderer
   */
  renderCard?: (property: Property, index: number) => React.ReactNode;
  
  /**
   * Enable/disable animations
   * @default true
   */
  animated?: boolean;
  
  /**
   * Gap between grid items
   * @default 'md'
   */
  gap?: 'sm' | 'md' | 'lg' | 'xl';
}

const gapClasses = {
  sm: 'gap-4',
  md: 'gap-6',
  lg: 'gap-8',
  xl: 'gap-12',
};

const columnClasses = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 md:grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
};

/**
 * Core Property Grid Component
 * 
 * Displays properties in a responsive grid layout.
 * Themes can customize appearance via renderCard prop or className.
 */
export function PropertyGrid({
  properties,
  columns = 3,
  maxRows,
  className = '',
  renderCard,
  animated = true,
  gap = 'md',
}: PropertyGridProps) {
  // Limit properties based on maxRows
  const displayProperties = maxRows 
    ? properties.slice(0, maxRows * columns)
    : properties;

  const gridClasses = `
    grid
    ${columnClasses[columns]}
    ${gapClasses[gap]}
    ${className}
  `.trim();

  return (
    <div className={gridClasses}>
      {displayProperties.map((property, index) => {
        const content = renderCard 
          ? renderCard(property, index)
          : <DefaultPropertyCard property={property} />;

        if (!animated) {
          return <div key={property.id}>{content}</div>;
        }

        return (
          <motion.div
            key={property.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ 
              duration: 0.5, 
              delay: index * 0.1,
              ease: [0.25, 0.46, 0.45, 0.94] 
            }}
          >
            {content}
          </motion.div>
        );
      })}
    </div>
  );
}

/**
 * Default Property Card
 * Simple, clean card used when no custom renderCard is provided
 * Note: Themes should use next/image in their custom renderCard implementations
 */
function DefaultPropertyCard({ property }: { property: Property }) {
  return (
    <div className="property-card">
      <div className="property-card-image">
        <div className="w-full h-full bg-gray-200" />
      </div>
      <div className="property-card-content">
        <h3 className="property-card-title">{property.name}</h3>
        <p className="property-card-location">{property.reference}</p>
      </div>
    </div>
  );
}
